/**
 * THE RACE RADIO — D-EZ-111, "the broadcast booth with characters".
 *
 * Howard, 5 Oct 2026: "the commentary sucks". It did: every lead change was
 * the same flat sentence, the booth spoke on twenty-seven turns a race with
 * nothing to say on most of them, and nobody in it remembered anything. This
 * rebuild keeps every guarantee the old radio had and changes what it sounds
 * like:
 *
 *   • A FIXED CAST PER LEAGUE (`cast.ts`): a play-by-play voice with a style,
 *     an ex-driver analyst with a past, a pick and grudges, a pit reporter who
 *     gets a word from the driver.
 *   • FEWER, BIGGER CALLS. Quiet stretches stay quiet. Every lead change,
 *     caution, pit stop and big retirement is still called (`mustSpeak`), but a
 *     back-marker's mechanical failure is gathered into a roundup, and the
 *     filler beats ("X has led 6 turns") are gone. Register follows the moment:
 *     short bursts late and at the flag, longer reads under the caution.
 *   • CALLBACKS AND STORYLINES (`storylines.ts`): a pre-race open sets up two
 *     or three of today's stories from history up to the previous race; the
 *     booth comes back to them when the dice touch them (and when rivals end up
 *     near each other); the wrap says how each one came out.
 *
 * THE RULES IT KEEPS:
 *   • It reads what the dice made. No die is drawn; every choice of words is an
 *     FNV hash of stable ids (`kit.rotate`).
 *   • No look-ahead. A beat at turn t reads turns 1…t, the starting grid and the
 *     league as it stood before this race. Which turns speak is decided online.
 *     The wrap reads the finished race, and airs at the flag.
 *   • Every name and number in a written line is also a Fact on its beat, so
 *     the grounding test runs every written line through `verifyLine`.
 */
import type { RaceResult } from '../race.ts';
import type { Car } from '../types.ts';
import type { ChallengeResult, ChallengeSequenceResult } from '../challenge.ts';
import type { BoothBeat, RaceCall } from './booth.ts';
import type { BoothContext, BoothSeasonContext } from './context.ts';
import type { Fact } from './researcher.ts';
import type { Angle } from './director.ts';
import { factsFor } from './researcher.ts';
import { lapsPerTurn, ordinal, registerOf } from './register.ts';
import { BIG_MOMENT_SCORE } from './director.ts';
import { finishSeasonLine, pointsPictureLine } from './seasonLines.ts';
import { DEFAULT_CAST, type MemberId } from './cast.ts';
import { exhibitionLeague, leagueSeq, type BoothLeague } from './league.ts';
import { analystPick, anecdote, grudgeLine, lastPickLine, pastFacts, pickNow, rivalMemory, takeLine } from './analyst.ts';
import { openStorylines, pairHistory, recordFor, resolveStoryline, type Storyline } from './storylines.ts';
import { cap, fact, fillSlots, joinNames, ordWord, rotate, spokenTrack, surname } from './kit.ts';
import * as C from './calls.ts';
import { incidentsFromTape, type Incident } from '../people/memory.ts';
import { quote, type QuoteFact } from '../people/quotes.ts';
import { rivalsNear } from '../people/rivalry.ts';
import { hash as stableHash } from './kit.ts';
import { buildBible, type StoryBible } from './bible.ts';
import { CHURN_LINE_MOVES, churnLine, churnStory } from '../churnStory.ts';

export interface BroadcastStory {
  headline: string;
  category: 'start' | 'lead' | 'incident' | 'pit' | 'battle' | 'restart' | 'running' | 'finish';
  /** The rest of the booth answering the call, in speaking order. */
  voices: { who: MemberId; line: string }[];
  stakes: string;
  /** D-EZ-111 — 1 calm · 2 lively · 3 the booth on its feet. */
  energy?: 1 | 2 | 3;
  /** D-EZ-111 — which bank lines were used and whom they were about (the variety harness counts these). */
  templates?: { tpl: string; ids: string[] }[];
  /** D-EZ-111 — the open only: today's storylines, as the booth set them up. */
  storylines?: string[];
}

const id = (car?: Car) => car?.card.driverId;
const name = (car?: Car) => car?.card.name ?? 'The leader';

/** Compact status always comes from the displayed turn, never the queued finish. */
export function raceStatus(race: RaceResult, cursor: number): { flag: string; leader: string; distance: string } {
  const finished = cursor >= race.turnsRun;
  const state = cursor > 0 ? race.turns[cursor - 1]?.state : undefined;
  const winner = race.finishingOrder.find(c => c.driverId === race.winnerId);
  const pole = [...race.state.grid, ...race.state.retired].find(c => id(c) === race.startingOrder?.[0]);
  const remaining = Math.max(0, race.scheduledTurns - cursor);
  return {
    flag: finished ? 'Finished' : cursor === 0 ? 'Ready' : state?.yellowTurnsRemaining ? 'Caution' : 'Green',
    leader: finished ? winner?.name ?? 'Undecided' : cursor === 0 ? (pole ? name(pole) : 'Grid set') : state?.grid[0] ? name(state.grid[0]) : 'Awaiting ruling',
    distance: finished ? 'Complete' : remaining > 0 ? `${remaining} ${remaining === 1 ? 'turn' : 'turns'} to go` : 'Overtime',
  };
}

/** Only useful, current stakes. Finish rules belong near the finish. */
export function raceSituation(race: RaceResult, cursor: number): string {
  const status = raceStatus(race, cursor);
  if (cursor >= race.turnsRun) return `${status.leader} wins the ${race.state.raceName.replace(/^the /i, "")}.`;
  if (cursor === 0) return `${race.scheduledTurns} scheduled turns.`;
  const state = race.turns[cursor - 1]?.state;
  if (!state?.grid.length) return '';
  const [leader, second] = state.grid;
  const remaining = race.scheduledTurns - cursor;
  if (state.yellowTurnsRemaining > 0) return remaining <= 3
    ? `${name(leader)} leads under caution. The race must return to green before the finish.`
    : `${name(leader)} leads under caution${second ? `, with ${name(second)} second` : ''}.`;
  if (state.pitTurns.includes(cursor + 1)) return `${cursor + 1 === state.pitTurns.at(-1) ? 'The final scheduled stops are' : 'Pit stops are'} next.`;
  return `${status.distance}.${second ? ` ${name(second)} runs second.` : ''}`;
}

function challengeExplanation(c: ChallengeResult, who: (id: string) => string): string | null {
  const a = who(c.challengerId), b = c.defenderId ? who(c.defenderId) : 'the defender';
  if (c.challengerLapped) return `${a}'s challenge is about recovering lap status, not taking ${b}'s place.`;
  if (c.grantUsed) return `${a}'s one-use automatic challenge win carries this attempt.`;
  if (c.chip === 'challenger-spent') return `${a} spends a saved performance chip to make the move stand.`;
  if (c.chip === 'defender-spent') return `${b} spends a saved performance chip to keep ${a} behind.`;
  if (c.chip === 'both-spend') return `${a} and ${b} each spend a performance chip; the original challenge result stands.`;
  if (c.success && !c.rawSuccess) return `${a}'s held chip advantage overturns the initial result.`;
  if (!c.success && c.rawSuccess) return `${b}'s held chip advantage stops a move that initially succeeded.`;
  return null;
}

const NUM = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const toGoWords = (k: number): string => (k <= 10 ? NUM[Math.max(0, k)] : String(k));

/** Optional beats a race (the run to the flag is not counted): quiet stretches stay quiet. */
const OPTIONAL_PER_RACE = 2;

/** Incidents a single turn produced, safe to read at that turn (not the race-long battle counts). */
const TURN_INCIDENTS = new Set(['wrecked', 'duel', 'duel-failed', 'feud-flare', 'shared-wreck']);

/** A league built from the season context alone — the path for callers that only have `season`. */
function seasonOnlyLeague(season: BoothSeasonContext): BoothLeague {
  return { ...exhibitionLeague(), key: 'season', seasonNumber: season.seasonNumber, raceNumber: season.raceNumber, seasonRaces: 36, season };
}

/**
 * One beat while it is being written: the lead line, the answers, and every
 * fact either of them rests on.
 */
class Draft {
  lead: { who: MemberId; text: string } | null = null;
  voices: { who: MemberId; line: string }[] = [];
  facts: Fact[] = [];
  templates: { tpl: string; ids: string[] }[] = [];
  energy: 1 | 2 | 3 = 1;
  constructor(readonly maxVoices: number) {}
  say(who: MemberId, text: string, tpl: string, ids: string[], facts: Fact[] = [], separate = false): boolean {
    if (!text) return false;
    if (!this.lead) this.lead = { who, text };
    else {
      const last = this.voices[this.voices.length - 1];
      // The same seat twice in a row is one person talking: join it (unless it is a new thought, like the sign-off).
      if (last && last.who === who && !separate) last.line = `${last.line} ${text}`;
      else if (this.voices.length >= this.maxVoices) return false;
      else this.voices.push({ who, line: text });
    }
    this.templates.push({ tpl, ids });
    this.facts.push(...facts);
    return true;
  }
  /** Add a sentence to the lead line. */
  append(text: string, tpl: string, ids: string[], facts: Fact[] = []) {
    if (!this.lead || !text) return;
    this.lead.text = `${this.lead.text}${text.startsWith(' ') ? '' : ' '}${text}`.replace(/\s+/g, ' ').trim();
    this.templates.push({ tpl, ids });
    this.facts.push(...facts);
  }
  room(): boolean {
    return this.voices.length < this.maxVoices;
  }
}

export function buildBroadcast(race: RaceResult, ctx: BoothContext = {}): RaceCall {
  const beats: BoothBeat[] = [];
  const allCars = [...race.state.grid, ...race.state.retired];
  // Final cards are used only to resolve stable names, never positions or ratings.
  const who = (driverId: string) => allCars.find(c => id(c) === driverId)?.card.name ?? driverId;
  // The short name: the surname, unless two in this field share it.
  const surnameCount = new Map<string, number>();
  for (const c of allCars) surnameCount.set(surname(c.card.name), (surnameCount.get(surname(c.card.name)) ?? 0) + 1);
  const sur = (driverId: string) => {
    const s = surname(who(driverId));
    return (surnameCount.get(s) ?? 0) > 1 ? who(driverId) : s;
  };
  const league: BoothLeague | null = ctx.league ?? (ctx.season ? seasonOnlyLeague(ctx.season) : null);
  const cast = league?.cast ?? DEFAULT_CAST;
  const style = cast.hostStyle;
  const raceKey = race.state.raceId ?? race.state.raceName;
  const lk = league ? `${league.key}|${league.seasonNumber}|${league.raceNumber}` : `exhibition|${raceKey}`;
  const seq = leagueSeq(league, stableHash(lk) % 997);
  /** The STABLE key: line rotation walks a bank week to week only if the key does not change with the race. */
  const sk = league?.key ?? `exhibition|${raceKey}`;
  const occ = new Map<string, number>();
  const usedToday = new Map<string, Set<number>>();
  /** Rotate within a bank; `slot` names the bank, `about` keys it to a driver. */
  const R = <T,>(bank: readonly T[], slot: string, about = '') => {
    const k = `${slot}|${about}`;
    const n = occ.get(k) ?? 0;
    occ.set(k, n + 1);
    const p = rotate(bank, `${league?.key ?? raceKey}|${k}`, seq, n);
    // Never the same line out of one bank twice in a race while the bank has
    // another one left (reads only what has already aired today).
    const used = usedToday.get(slot) ?? new Set<number>();
    let index = p.index;
    for (let i = 0; i < bank.length && used.has(index); i++) index = (index + 1) % bank.length;
    used.add(index);
    usedToday.set(slot, used);
    return { item: bank[index], tpl: `${slot}#${index}` };
  };
  const fill = fillSlots;
  const teamOf = (driverId: string): string | null => {
    const car = allCars.find(c => id(c) === driverId);
    const t = car?.card.teamId;
    return t && ctx.teams ? ctx.teams.get(t) ?? null : null;
  };
  const trackSpoken = spokenTrack(race.track.name);
  const now = { seasonNumber: league?.seasonNumber ?? 0, raceNumber: league?.raceNumber ?? 0 };
  const venue = league?.race?.venue ?? null;

  const push = (turn: number, d: Draft, story: Omit<BroadcastStory, 'voices' | 'energy' | 'templates'>, opts: { kind?: BoothBeat['kind']; angle?: Angle; citation?: string | null } = {}) => {
    if (!d.lead) return;
    const line = d.lead.text;
    const statements = [...d.facts];
    // Who says the lead line is decided by `roleFor` in `lib/narrate.ts` from
    // the beat's kind and angle — so the angle follows the seat that spoke.
    const seatAngle: Angle = d.lead.who === 'pit' ? 'consequence' : d.lead.who === 'analyst' ? 'rivalry' : opts.angle === 'consequence' || opts.angle === 'rivalry' ? 'move' : opts.angle ?? 'move';
    beats.push({
      turn,
      kind: opts.kind ?? 'moment',
      angle: seatAngle,
      line,
      fellBack: false,
      facts: statements.slice(0, 3),
      moreFacts: statements,
      why: null,
      ruleCitation: opts.citation ?? null,
      turnsCovered: 1,
      score: d.energy === 3 ? BIG_MOMENT_SCORE : d.energy === 2 ? 30 : 12,
      broadcast: { ...story, voices: d.voices, energy: d.energy, templates: d.templates },
    });
  };

  // ======================================================================
  // THE OPEN — before the green. Reads the grid and the league going in.
  // ======================================================================
  const starters = race.startingOrder ?? [];
  const opened: Storyline[] = openStorylines(league, { order: starters, nameOf: who, shortOf: sur, venue }, seq);
  const pick = analystPick(cast, league?.season ?? null, starters, who, lk);
  // The pole is the analyst's to talk about when he picks the pole sitter.
  const stories = opened.filter(s => !(s.kind === 'pole' && s.ids[0] === pick?.driverId));
  const storyOf = new Map<string, Storyline>();
  for (const s of stories) for (const x of s.ids) if (!storyOf.has(x)) storyOf.set(x, s);
  const fieldSize = allCars.length;
  let pickLine: string | null = null;
  {
    const d = new Draft(4);
    d.energy = 2;
    if (starters.length) {
      const w = R(C.WELCOME[style], 'welcome');
      let text = fill(w.item, { race: race.state.raceName, track: trackSpoken, field: fieldSize, P: who(starters[0]) });
      const wf = [fact('race-field', `${fieldSize} cars start the ${race.state.raceName} at ${trackSpoken}; ${who(starters[0])} on the pole`, fieldSize, [starters[0]]), fact('race-field', `${who(starters[0])} starts first`, 1, [starters[0]])];
      if (league && league.seasonNumber > 0 && league.raceNumber > 0 && league.seasonRaces > 0) {
        const y = R(C.WHERE_IN_YEAR, 'year');
        text += fill(y.item, { n: league.raceNumber, N: league.seasonRaces });
        wf.push(fact('season', `race ${league.raceNumber} of ${league.seasonRaces}`, league.raceNumber), fact('season', `${league.seasonRaces} races in the season`, league.seasonRaces));
      }
      d.say('host', text, w.tpl, [starters[0]], wf);
    } else {
      d.say('host', `${race.state.raceName} is under way.`, 'welcome-bare', []);
    }
    // The booth passes the storylines between them: the host sets up the first,
    // the analyst takes the second, the host the third — then the analyst's pick.
    const seats: MemberId[] = ['host', 'analyst', 'host'];
    stories.forEach((s, i) => {
      if (i === 0) d.append(s.text, s.tpl, s.ids, s.facts);
      else d.say(seats[i] ?? 'host', s.text, s.tpl, s.ids, s.facts);
    });
    // The analyst's take: last week owned, this week's pick.
    if (pick) {
      const parts: string[] = [];
      const facts: Fact[] = [...pick.facts, fact('take', `the analyst picks ${pick.name}`, 0, [pick.driverId])];
      const tpls: string[] = [];
      if (league?.lastPick) {
        const lp = lastPickLine(league.lastPick, sk, seq);
        parts.push(lp.text);
        facts.push(...lp.facts, fact('take', `${league.lastPick.name} finished ${league.lastPick.place ?? 'out'} in the ${league.lastPick.raceName}`, league.lastPick.place ?? 0, [league.lastPick.driverId]));
        tpls.push(lp.tpl);
      }
      const t = takeLine(cast, pick, sk, seq, league?.lastPick?.driverId === pick.driverId, stories.some(s => s.ids[0] === pick.driverId && s.kind !== 'rivalry'));
      parts.push(t.text);
      tpls.push(t.tpl);
      pickLine = parts.join(' ');
      d.say('analyst', pickLine, tpls.join('+'), [pick.driverId], facts);
    }
    push(0, d, { headline: 'Before the green', category: 'start', stakes: raceSituation(race, 0), storylines: stories.map(s => s.text) }, { kind: 'open' });
  }

  // ======================================================================
  // THE RACE — turn by turn, online.
  // ======================================================================
  const people = league?.people ?? null;
  const incidents: Incident[] = (() => {
    try {
      return (incidentsFromTape({ race } as never) ?? []).filter(i => TURN_INCIDENTS.has(i.k));
    } catch {
      return [];
    }
  })();
  const rivalRecord = (a: string, b: string) => recordFor(league, a, b);
  const isRivals = (a: string, b: string) => {
    const r = rivalRecord(a, b);
    return !!r && (r.state === 'boiling' || r.state === 'hot' || r.state === 'simmering');
  };
  const pending: string[] = [];          // minor retirements not yet named
  /** A pair's history is told once a race — the second time it is a rerun. */
  const toldPairs = new Set<string>();
  // The open already told the storyline rivalries' history.
  const openedPairs = new Set(stories.filter(s => s.kind === 'rivalry').map(s => [...s.ids].sort().join('|')));
  for (const k of openedPairs) toldPairs.add(k);
  const tellPair = (rec: NonNullable<ReturnType<typeof recordFor>>) => {
    const k = `${rec.a}|${rec.b}`;
    if (toldPairs.has(k)) return null;
    const h = pairHistory(rec, who, now, venue, sk, seq, sur);
    if (h) toldPairs.add(k);
    return h;
  };
  const called = new Set<string>();      // storyline / pair / read keys already used today
  let optionalAired = 0;
  let lastOptional = -99;
  let pointsAired = false;
  let priorIds = starters;
  let priorLeader: string | undefined = priorIds[0];

  const roundup = (d: Draft, bank: readonly string[], slot: string) => {
    if (!pending.length || !d.room()) return;
    const ids = pending.splice(0, pending.length);
    const r = R(bank, slot);
    d.say('pit', fill(r.item, { names: joinNames(ids.map(who)), verb: ids.length === 1 ? 'is' : 'are' }), r.tpl, ids, ids.map(x => fact('cars-retired', `${who(x)} is out of the race`, 0, [x])));
  };

  for (const turn of race.turns) {
    const t = turn.turn, state = turn.state, [leader, runnerUp] = state.grid;
    const leaderChanged = !!priorLeader && id(leader) !== priorLeader;
    const wasYellow = t > 1 && race.turns[t - 2].kind === 'yellow';
    const restarting = wasYellow && turn.kind !== 'yellow';
    const toGo = race.scheduledTurns - t;
    const late = toGo <= Math.max(2, Math.round(race.scheduledTurns * 0.15));
    const positions = new Map(priorIds.map((driverId, i) => [driverId, i + 1]));
    const posNow = (x: string) => state.grid.findIndex(c => id(c) === x) + 1;
    const children = turn.events.filter(e => e.eventType === 'quality-challenge-sequence').flatMap(e => (e as ChallengeSequenceResult).challenges);
    const contest = children.filter(c => !c.voided && c.defenderId).sort((a, b) => {
      const priority = (c: ChallengeResult) => (c.challengerId === id(leader) ? 8 : 0) + (c.defenderId === priorLeader ? 6 : 0) + (c.success ? 2 : 0);
      return priority(b) - priority(a);
    })[0];
    const retired = turn.retiredThisTurn;
    const crash = turn.collected.length >= 3;
    const important = (x: string) => crash || (positions.get(x) ?? 99) <= 10 || storyOf.has(x) || x === pick?.driverId || x === priorLeader;
    const bigOut = retired.filter(important);
    for (const x of retired) if (!important(x)) pending.push(x);
    const turnIncidents = incidents.filter(i => i.t === t);
    const rivalHit = turnIncidents.find(i => isRivals(i.a, i.b));
    const kToGo = { togo: `${toGoWords(toGo)} to go`, Togo: cap(`${toGoWords(toGo)} to go`) };
    const toGoFact = fact('race-status', `${toGo} turns to go`, toGo);

    // The turns that MUST speak: every lead change, caution and pit stop, any
    // retirement that matters, a big crash, and two rivals at it.
    const mustSpeak = leaderChanged || !!turn.leadChangedTo || turn.yellowCameOut || !!turn.pit || bigOut.length > 0 || turn.collected.length >= 3 || !!rivalHit;

    // Optional beats: rationed (four a race, three turns apart), read online.
    let optional: 'restart' | 'rivals' | 'held' | 'flag' | 'climb' | null = null;
    let near: ReturnType<typeof rivalsNear>[number] | null = null;
    let climber: string | null = null;
    if (!mustSpeak && t < race.turnsRun) {
      const room = optionalAired < OPTIONAL_PER_RACE && t - lastOptional >= 4;
      if (people && room) {
        near = rivalsNear(state.grid.slice(0, 12).map(c => c.card.driverId), people.book, 2)
          .find(p => (p.record.state === 'boiling' || p.record.state === 'hot') && !called.has(`near|${p.a}|${p.b}`) && !called.has(`near|${p.b}|${p.a}`) && (openedPairs.has(`${p.record.a}|${p.record.b}`) || !toldPairs.has(`${p.record.a}|${p.record.b}`))) ?? null;
      }
      const leadContest = contest && contest.defenderId === id(leader) && !contest.success && !contest.challengerLapped;
      const climb = state.grid.slice(0, 3).map(c => c.card.driverId).find(x => (storyOf.has(x) || x === pick?.driverId) && (positions.get(x) ?? 99) > 3 && !called.has(`climb|${x}`));
      if (restarting && late && room && toGo >= 1) optional = 'restart';
      else if (late && leadContest && room) optional = 'held';
      else if (toGo === 1) optional = 'flag';
      else if (near && room) optional = 'rivals';
      else if (climb && room && t > 2) { optional = 'climb'; climber = climb; }
    }
    if (!mustSpeak && !optional) {
      priorIds = state.grid.map(c => c.card.driverId); priorLeader = id(leader);
      continue;
    }

    // The longer read belongs on the call that brings the caution out.
    const underYellow = turn.yellowCameOut;
    const d = new Draft(turn.yellowCameOut ? 2 : 1);
    let category: BroadcastStory['category'] = 'running';
    let headline = '';
    let angle: Angle = 'move';
    const kind: BoothBeat['kind'] = 'moment';
    let citation = turn.events.find(e => e.actorIds.includes(id(leader) ?? ''))?.ruleCitation ?? null;
    const L = name(leader), S = name(runnerUp);
    const leaderIds = [id(leader), id(runnerUp)].filter((x): x is string => !!x);
    const orderFacts = [fact('race-status', `${L} leads`, 1, leaderIds.slice(0, 1)), ...(runnerUp ? [fact('race-status', `${S} runs second`, 2, [id(runnerUp)!])] : [])];

    // ---- the lead call -------------------------------------------------------
    const leaderOut = !!priorLeader && retired.includes(priorLeader);
    if (bigOut.length || leaderOut || turn.collected.length >= 3 || (turn.yellowCameOut && !leaderChanged)) {
      category = 'incident';
      const n = turn.collected.length;
      const nFact = fact('cars-out', `${n} cars caught up`, n);
      if (leaderOut) {
        d.energy = 3;
        headline = 'The leader is out';
        const r = R(C.LEADER_OUT[style], 'leader-out');
        d.say('host', fill(r.item, { L: who(priorLeader!) }), r.tpl, [priorLeader!], [fact('cars-retired', `${who(priorLeader!)} was leading and is out of the race`, 0, [priorLeader!])]);
      } else if (n >= 3) {
        d.energy = 3;
        headline = 'A big crash';
        const r = R(C.BIG_CRASH[style], 'big-crash');
        d.say('host', fill(r.item, { n }), r.tpl, [], [nFact]);
      } else if (turn.yellowCameOut) {
        d.energy = 2;
        headline = 'The race goes yellow';
        const r = n >= 2 ? R(C.CAUTION_N, 'caution-n') : R(C.CAUTION[style], 'caution');
        d.say('host', fill(r.item, { n }), r.tpl, [], n ? [nFact] : []);
      } else {
        d.energy = 2;
        headline = 'Retirement';
      }
      const named = (leaderOut ? retired.filter(x => x !== priorLeader) : retired).filter(important);
      if (named.length) {
        const r = R(C.OUT, 'out');
        // Three names in the call; pit road reads out the rest.
        const inCall = named.length > 4 ? named.slice(0, 3) : named;
        if (named.length > inCall.length) pending.unshift(...named.slice(inCall.length));
        const text = fill(r.item, { names: joinNames(inCall.map(who)), verb: inCall.length === 1 ? 'is' : 'are' }).trim();
        const fs = inCall.map(x => fact('cars-retired', `${who(x)} is out of the race`, 0, [x]));
        if (d.lead) d.append(text, r.tpl, inCall, fs);
        else d.say('host', cap(text), r.tpl, inCall, fs);
      }
      if (leaderChanged && !leaderOut) {
        d.append(`${L} leads now.`, 'lead-after-crash', leaderIds, orderFacts);
      } else if (leaderOut || turn.yellowCameOut) {
        const r = R(C.UNDER_YELLOW, 'under-yellow', id(leader));
        d.append(fill(r.item, { L, S }).trim(), r.tpl, leaderIds, orderFacts);
      }
      citation = turn.events.find(e => e.eventType === 'trouble' || e.eventType === 'problem')?.ruleCitation ?? citation;
    } else if (leaderChanged && !turn.pit) {
      category = 'lead';
      const from = priorLeader!;
      const former = posNow(from);
      const A = L, a = sur(id(leader)!), B = who(from), b = sur(from);
      const leadFacts = [fact('race-status', `${A} takes the lead from ${B}`, 1, [id(leader)!, from])];
      // HOW the lead changed decides the words: a pass is "goes by"; a leader
      // in trouble is not passed, he falls back; anything else is said plainly.
      const passed = children.some(c => c.success && c.challengerId === id(leader) && c.positionDeltas.some(x => x.driverId === id(leader) && x.to === 1));
      const trouble = !passed && (turn.collected.includes(from) || turn.events.some(e => (e.eventType === 'trouble' || e.eventType === 'problem') && e.actorIds.includes(from)));
      const how = passed ? '' : trouble ? 'inherit' : 'neutral';
      const dropOf = (): { drop: string; facts: Fact[] } => {
        if (former === 2) { const q = R(C.DROP_SECOND, 'drop2', from); return { drop: fill(q.item, { b }), facts: [fact('race-status', `${B} second`, 2, [from])] }; }
        if (former > 2) { const q = R(C.DROP, 'drop', from); return { drop: fill(q.item, { b, pos: ordWord(former) }), facts: [fact('position-loss', `${B} drops to ${former}`, former, [from])] }; }
        return { drop: '', facts: [] };
      };
      if (late || toGo <= 0) {
        d.energy = 3;
        headline = `${A} takes the lead late`;
        const words = toGo > 0 ? kToGo : toGo === 0 ? { togo: 'on the final turn', Togo: 'On the final turn' } : { togo: 'in overtime', Togo: 'In overtime' };
        if (toGo === 0 && passed) {
          const r = R(C.FINAL_LEAD[style], 'final-lead', id(leader));
          const dr = former > 1 ? dropOf() : { drop: '', facts: [] };
          d.say('host', fill(r.item, { A, a, B, b, drop: dr.drop }), r.tpl, [id(leader)!, from], [...leadFacts, ...dr.facts]);
        } else {
          const bank = how === 'inherit' ? C.INHERIT_LATE : how === 'neutral' ? C.NEUTRAL_LATE : C.LATE_LEAD[style];
          const r = R(bank, how ? `${how}-late` : 'late-lead', id(leader));
          d.say('host', fill(r.item, { A, a, B, b, ...words }), r.tpl, [id(leader)!, from], [...leadFacts, ...(toGo > 0 ? [toGoFact] : [])]);
          if (how === 'inherit' && former > 1) {
            const dr = dropOf();
            d.append(dr.drop, 'drop', [from], dr.facts);
          }
        }
      } else {
        d.energy = t <= Math.round(race.scheduledTurns * 0.3) ? 1 : 2;
        headline = `${A} takes the lead`;
        const bank = how === 'inherit' ? C.INHERIT : how === 'neutral' ? C.NEUTRAL : C.LEAD[style];
        const r = R(bank, how || 'lead', id(leader));
        const dr = dropOf();
        d.say('host', fill(r.item, { A, a, B, b, drop: dr.drop }), r.tpl, [id(leader)!, from], [...leadFacts, ...dr.facts]);
      }
      if (turn.yellowCameOut) d.append("And the caution's out.", 'caution-tail', [], []);
    } else if (turn.pit) {
      category = 'pit';
      angle = 'consequence';
      d.energy = 1;
      const lastStops = t === state.pitTurns.at(-1);
      headline = lastStops ? 'The last scheduled stops' : 'The race goes through pit road';
      const team = teamOf(id(leader)!);
      if (leaderChanged) {
        d.energy = 2;
        const r = team ? R(C.PIT_LEAD_TEAM, 'pit-lead-team', id(leader)) : R(C.PIT_LEAD, 'pit-lead', id(leader));
        d.say('pit', fill(r.item, { A: L, B: who(priorLeader!), team: team ?? '' }), r.tpl, [id(leader)!, priorLeader!], [fact('pit-stop', `${L} leaves pit road in the lead, ahead of ${who(priorLeader!)}`, 1, [id(leader)!, priorLeader!]), ...(team ? [fact('shop', `${L} drives for ${team}`, 0, [id(leader)!])] : [])]);
      } else {
        const r = team && stableHash(`${lk}|${t}`) % 2 === 0 ? R(C.PIT_HOLD_TEAM, 'pit-hold-team', id(leader)) : R(C.PIT_HOLD, 'pit-hold', id(leader));
        d.say('pit', fill(r.item, { L, team: team ?? '' }), r.tpl, [id(leader)!], [fact('pit-stop', `${L} keeps the lead through the stops`, 1, [id(leader)!]), ...(team ? [fact('shop', `${L} drives for ${team}`, 0, [id(leader)!])] : [])]);
      }
      if (lastStops) d.lead!.text = `Last scheduled stops of the day. ${d.lead!.text}`;
      const moves = state.grid.map((car, i) => ({ car, from: positions.get(id(car)!), to: i + 1 })).filter(m => m.from !== undefined && m.from - m.to >= 4 && m.to <= 15 && m.to > 1).sort((x, y) => (y.from! - y.to) - (x.from! - x.to));
      if (moves[0]) {
        const m = moves[0];
        const r = R(C.PIT_MOVER, 'pit-mover', id(m.car));
        d.append(fill(r.item, { M: name(m.car), to: ordWord(m.to), from: ordWord(m.from!) }), r.tpl, [id(m.car)!], [fact('position-gain', `${name(m.car)} from ${m.from} to ${m.to} on pit road`, m.to, [id(m.car)!]), fact('position-gain', `${name(m.car)} was ${m.from}`, m.from!, [id(m.car)!])]);
      }
    } else if (rivalHit) {
      category = 'battle';
      d.energy = 3;
      headline = 'These two again';
      const A = who(rivalHit.a), B = who(rivalHit.b);
      d.say('host', `${A} and ${B}, at it again!`, 'rival-hit', [rivalHit.a, rivalHit.b], [fact('rivalry', `${A} and ${B} tangle on turn ${t}`, t, [rivalHit.a, rivalHit.b])]);
    } else if (optional === 'restart') {
      category = 'restart';
      d.energy = 2;
      headline = 'Back to green';
      const r = R(C.RESTART[style], 'restart');
      d.say('host', fill(r.item, { L, S, ...kToGo }), r.tpl, leaderIds, [...orderFacts, toGoFact]);
    } else if (optional === 'held' && contest) {
      category = 'battle';
      d.energy = 2;
      headline = 'The leader holds';
      const r = R(C.HELD[style], 'held');
      d.say('host', fill(r.item, { C: who(contest.challengerId), c: sur(contest.challengerId), L, l: sur(id(leader)!) }), r.tpl, [contest.challengerId, id(leader)!], [fact('head-to-head', `${who(contest.challengerId)} challenges ${L} and the move does not stick`, 0, [contest.challengerId, id(leader)!])]);
    } else if (optional === 'flag') {
      d.energy = toGo <= 1 ? 3 : 2;
      headline = 'The run to the flag';
      const r = R(C.RUN_TO_FLAG[style], 'flag');
      d.say('host', fill(r.item, { L, S, l: sur(id(leader)!), s: runnerUp ? sur(id(runnerUp)!) : '', ...kToGo }), r.tpl, leaderIds, [...orderFacts, toGoFact]);
    } else if (optional === 'rivals' && near) {
      category = 'battle';
      angle = 'rivalry';
      d.energy = 2;
      headline = 'Rivals together';
      const pa = posNow(near.a), pb = posNow(near.b);
      const fromOpen = openedPairs.has(`${near.record.a}|${near.record.b}`);
      const h = fromOpen ? { text: R(["That's the pair we talked about before the green.", 'Remember the history we set up before the green? Here they are.', 'These are the two I told you to watch.'], 'near-open').item, facts: [] as Fact[], tpl: 'near-open' } : tellPair(near.record);
      const r = R(['Look who has found each other: {A} {pa}, {B} {pb}.', '{a} and {b} are running {pa} and {pb} right now.', 'Here are two who know each other: {A} and {B}, {pa} and {pb}.', "Well, well. {a}'s {pa} and {b}'s {pb}.", 'Somebody check the mirrors. {A} {pa}, {B} {pb}.', 'Now this is interesting: {a} {pa}, {b} {pb}.', 'Two men with history, nose to tail: {a} {pa}, {b} {pb}.'], 'near', near.a);
      d.say('analyst', `${fill(r.item, { A: who(near.a), B: who(near.b), a: sur(near.a), b: sur(near.b), pa: ordWord(pa), pb: ordWord(pb) })}${h ? ` ${h.text}` : ''}`, `${r.tpl}${h ? `+${h.tpl}` : ''}`, [near.a, near.b], [fact('race-status', `${who(near.a)} ${pa}, ${who(near.b)} ${pb}`, pa, [near.a, near.b]), fact('race-status', `${who(near.b)} ${pb}`, pb, [near.b]), ...(h?.facts ?? [])]);
      called.add(`near|${near.a}|${near.b}`);
    } else if (optional === 'climb' && climber) {
      category = 'battle';
      d.energy = 2;
      headline = `${who(climber)} moves forward`;
      const p = posNow(climber);
      d.say('host', `${who(climber)} is up to ${ordWord(p)}.`, 'climb', [climber], [fact('position-gain', `${who(climber)} is ${p}`, p, [climber])]);
      called.add(`climb|${climber}`);
    }
    if (!d.lead) {
      // A must-speak turn that fell through every branch (a lead change on a
      // pit turn already handled above cannot reach here) — say the state.
      d.say('host', `${L} leads${runnerUp ? `, ${S} second` : ''}.`, 'state', leaderIds, orderFacts);
    }
    if (optional && optional !== 'flag') { optionalAired++; lastOptional = t; }

    // ---- the answers ----------------------------------------------------------
    // Pit road first on trouble: why he is out, and a word from him.
    const mainOut = (leaderOut ? priorLeader : bigOut[0]) ?? null;
    if (mainOut && d.room()) {
      const car = state.retired.find(c => id(c) === mainOut);
      const reason = car?.outReason ? C.REASON[car.outReason] : undefined;
      let text = '';
      const tpls: string[] = [];
      const fs: Fact[] = [];
      if (reason && !(car?.outReason === 'trouble' && (d.lead?.text ?? '').includes(who(mainOut)))) {
        const r = R(reason, `reason-${car!.outReason}`);
        text = fill(r.item, { N: who(mainOut) });
        tpls.push(r.tpl);
        fs.push(fact('cars-retired', `${who(mainOut)} out: ${car!.outReason}`, 0, [mainOut]));
      }
      if (people) {
        const hit = turnIncidents.find(i => i.b === mainOut && i.a !== mainOut && (i.k === 'wrecked' || i.k === 'duel'));
        const qf: QuoteFact = hit
          ? { kind: 'wrecked-by', otherId: hit.a, raceId: raceKey, seasonNumber: league?.seasonNumber, raceNumber: league?.raceNumber, team: teamOf(mainOut) }
          : car?.outReason === 'problem' || car?.outReason === 'fuel' || car?.outReason === 'repair-failed'
            ? { kind: 'dnf-mechanical', raceId: raceKey, seasonNumber: league?.seasonNumber, raceNumber: league?.raceNumber, team: teamOf(mainOut) }
            : { kind: 'bad-day', raceId: raceKey, seasonNumber: league?.seasonNumber, raceNumber: league?.raceNumber, team: teamOf(mainOut) };
        const q = quote(qf, mainOut, people);
        if (q) {
          const r = R(C.QUOTE, 'quote');
          text = `${text} ${fill(r.item, { s: sur(mainOut), q: q.text })}`.trim();
          tpls.push(r.tpl);
          fs.push(fact('quote', `${who(mainOut)}: ${q.text}`, 0, [mainOut]));
          if (hit) fs.push(fact('rivalry', `${who(hit.a)} and ${who(mainOut)} tangled`, 0, [hit.a, mainOut]));
        }
      }
      if (text) d.say('pit', text, tpls.join('+'), [mainOut], fs);
    }

    // Rivals at it: the analyst has history, and an opinion.
    if (rivalHit && d.room() && !called.has(`hit|${rivalHit.a}|${rivalHit.b}`)) {
      const rec = rivalRecord(rivalHit.a, rivalHit.b);
      const h = rec ? tellPair(rec) : null;
      const g = (rivalHit.k === 'wrecked' || rivalHit.k === 'feud-flare') && (cast.past.grudge === 'wreckers' || cast.past.grudge === 'hotheads')
        ? grudgeLine(cast, sk, seq)
        : rivalMemory(cast, sur(rivalHit.a), sur(rivalHit.b), sk, seq);
      // Say what they did TODAY first, unless the call already did.
      const A = sur(rivalHit.a), B = sur(rivalHit.b);
      const TODAY: Record<string, string> = {
        wrecked: rivalHit.d === 'self-out' ? `${A} went after ${B}, and it was ${A} who ended up in the wall.` : `${A} just put ${B} in the wall.`,
        duel: `${A} beat ${B} in a duel there and left him in trouble.`,
        'duel-failed': `${A} went after ${B}, and it backfired on him.`,
        'feud-flare': `The feud between ${A} and ${B} just flared up again.`,
        'shared-wreck': `${A} and ${B} were both caught up in that.`,
      };
      const lead = d.lead?.text ?? '';
      const today = lead.includes(A) && lead.includes(B) ? '' : TODAY[rivalHit.k] ?? '';
      const text = [today, h ? `They've been here before. ${h.text}` : '', g.text].filter(Boolean).join(' ');
      d.say('analyst', text, `${h?.tpl ?? ''}+${g.tpl}`, [rivalHit.a, rivalHit.b], [...(h?.facts ?? []), ...pastFacts(cast), fact('rivalry', `${who(rivalHit.a)} and ${who(rivalHit.b)} tangle today`, 0, [rivalHit.a, rivalHit.b])]);
      called.add(`hit|${rivalHit.a}|${rivalHit.b}`);
    }

    // The pick, and the storylines, when the dice touch them.
    if (pick && d.room()) {
      if (category === 'lead' && id(leader) === pick.driverId && !called.has('pick-leads')) {
        const p = pickNow('leads', pick.name, null, sk, seq, sur(pick.driverId));
        d.say('analyst', p.text, p.tpl, [pick.driverId], [fact('take', `the analyst picked ${pick.name}, who now leads`, 0, [pick.driverId])]);
        called.add('pick-leads');
      } else if (retired.includes(pick.driverId) && !called.has('pick-out')) {
        const p = pickNow('out', pick.name, null, sk, seq, sur(pick.driverId));
        d.say('analyst', p.text, p.tpl, [pick.driverId], [fact('take', `the analyst picked ${pick.name}, who is out`, 0, [pick.driverId])]);
        called.add('pick-out');
      }
    }
    if (d.room() && (category === 'lead' || optional === 'climb')) {
      const subject = optional === 'climb' ? climber : id(leader);
      const s = subject ? storyOf.get(subject) : undefined;
      if (s && subject && !called.has(`story|${s.key}`) && subject !== pick?.driverId) {
        const cb = storyCallback(s, subject, who, sk, seq);
        if (cb) { d.say('analyst', cb.text, cb.tpl, [subject], [...s.facts, ...cb.facts]); called.add(`story|${s.key}`); }
      }
    }
    if (d.room() && category === 'lead' && priorLeader && isRivals(id(leader)!, priorLeader) && !called.has(`pass|${id(leader)}|${priorLeader}`)) {
      const rec = rivalRecord(id(leader)!, priorLeader)!;
      const h = tellPair(rec);
      if (h) { d.say('analyst', `And these two have history. ${h.text}`, `pass+${h.tpl}`, [id(leader)!, priorLeader], h.facts); called.add(`pass|${id(leader)}|${priorLeader}`); }
    }

    // How the move was made, when it cost something.
    const featured = category === 'lead'
      ? children.find(c => c.success && c.challengerId === id(leader) && c.positionDeltas.some(x => x.driverId === id(leader) && x.to === 1))
      : contest && (contest.defenderId === priorLeader || contest.challengerId === id(leader)) ? contest : undefined;
    if (featured && d.room() && (category === 'lead' || optional === 'held')) {
      const explanation = challengeExplanation(featured, who);
      if (explanation) {
        d.say('analyst', explanation, 'chip', [featured.challengerId, featured.defenderId ?? ''].filter(Boolean), [fact('chips', explanation, 0, [featured.challengerId])]);
        citation = featured.ruleCitation;
      }
    }

    // Pit road: penalties and chips.
    if (turn.pit && d.room()) {
      const penalty = turn.pit.violations[0];
      if (penalty) {
        const pos = posNow(penalty.driverId);
        const r = R(C.PIT_PENALTY, 'penalty');
        d.say('pit', fill(r.item, { P: who(penalty.driverId), pos: pos ? ordWord(pos) : 'the back' }), r.tpl, [penalty.driverId], [fact('pit-penalty', `${who(penalty.driverId)} pit-road violation, now ${pos}`, pos, [penalty.driverId])]);
        if (cast.past.grudge === 'pit-road' && !called.has('grudge') && d.room()) {
          const g = grudgeLine(cast, sk, seq);
          d.say('analyst', g.text, g.tpl, [], pastFacts(cast));
          called.add('grudge');
        }
      } else if (turn.pit.fastPit.rolls.some(r => r.earned)) {
        const earned = turn.pit.fastPit.rolls.filter(r => r.earned).sort((a, b) => state.grid.findIndex(c => id(c) === a.driverId) - state.grid.findIndex(c => id(c) === b.driverId)).slice(0, 2);
        const names = joinNames(earned.map(r => who(r.driverId)));
        const r = R(C.CHIP_EARNED, 'chip-earned', earned[0].driverId);
        d.say('pit', fill(r.item, { names, verb: earned.length === 1 ? 'earns' : 'earn', bank: earned.length === 1 ? 'banks' : 'bank' }), r.tpl, earned.map(r => r.driverId), earned.map(r => fact('chips', `${who(r.driverId)} earns a chip at the stop`, 0, [r.driverId])));
      }
    }

    // The shuffle at the back (EZRA-S2 §3.12). Howard, 5 Oct 2026: the Churn's
    // rolls are no longer walked one by one at the table, so the pit reporter
    // gives it the one plain line it deserves — and only when it moved someone.
    if (turn.churn?.occurred && d.room()) {
      const story = churnStory(turn);
      const line = churnLine(turn, (x) => who(x));
      if (story && line && (story.moves.length || story.porch.length || story.lapBack.length || story.duel)) {
        const fs: Fact[] = [
          ...story.moves.flatMap((m) => [fact('position-gain', `${who(m.driverId)} climbs to ${ordinal(m.to)} at the back`, m.to, [m.driverId]), fact('position-gain', `${who(m.driverId)} was ${ordinal(m.from)}`, m.from, [m.driverId])]),
          ...story.porch.map((p) => fact('chips', `${who(p.driverId)} beats ${who(p.passedId)} in a run at 19th and earns a chip`, 19, [p.driverId, p.passedId])),
          ...story.lapBack.map((l) => fact('position-gain', `${who(l.driverId)} gets his lap back and runs ${ordinal(l.to)}`, l.to, [l.driverId])),
          ...(story.duel ? [fact('position-loss', `a duel at the back between ${who(story.duel.instigatorId)} and ${who(story.duel.targetId)}`, 0, [story.duel.instigatorId, story.duel.targetId])] : []),
        ];
        const more = story.moves.length - CHURN_LINE_MOVES;
        if (more > 0) fs.push(fact('position-gain', `${more} more cars move up at the back`, more));
        d.say('pit', line, 'churn', [...new Set(fs.flatMap((f) => f.driverIds))], fs);
      }
    }

    // Injury and feud facts from the season's ledger.
    const ids = [id(leader), contest?.challengerId, contest?.defenderId, ...retired].filter((x): x is string => !!x);
    const research = factsFor(race, turn, ids, race.track, ctx).filter(f => f.kind === 'injury-cost' || f.kind === 'feud');
    if (research[0] && d.room() && !(research[0].kind === 'feud' && d.voices.some(v => v.who === 'analyst'))) {
      const extra = research[0];
      d.say(extra.kind === 'injury-cost' ? 'pit' : 'analyst', cap(extra.text.replace(/[.!?]+$/, '') + '.'), `research-${extra.kind}`, extra.driverIds, [extra]);
    }

    // UNDER THE CAUTION THE BOOTH TALKS. One longer read, never the same kind twice a race.
    if (underYellow && d.room() && d.lead?.who !== 'analyst' && !d.voices.some(v => v.who === 'analyst')) {
      const read = cautionRead(d.energy === 3);
      if (read) d.say(read.who, read.text, read.tpl, read.ids, read.facts);
    }
    roundup(d, C.ROUNDUP, 'roundup');

    const stakes = t < race.turnsRun ? raceSituation(race, t) : '';
    push(t, d, { headline, category, stakes }, { kind, angle: category === 'pit' ? 'consequence' : angle, citation });
    priorIds = state.grid.map(c => c.card.driverId); priorLeader = id(leader);

    // -------------------------------------------------------------------------
    function cautionRead(big: boolean): { who: MemberId; text: string; tpl: string; ids: string[]; facts: Fact[] } | null {
      // After a big crash the booth talks about the crash and who it touched, not about old times.
      const order = big ? (['pick', 'story'] as const) : (['story', 'pick', 'points', 'near', 'anecdote'] as const);
      const start = stableHash(`${lk}|read|${t}`) % order.length;
      for (let i = 0; i < order.length; i++) {
        const k = order[(start + i) % order.length];
        if (called.has(`read-${k}`)) continue;
        if (k === 'story') {
          const s = stories.find(x => x.kind !== 'rivalry' && x.ids[0] && posNow(x.ids[0]) > 2 && !called.has(`story|${x.key}`));
          if (!s) continue;
          const x = s.ids[0];
          const p = posNow(x);
          const rm = storyReminder(s, who);
          called.add('read-story'); called.add(`story|${s.key}`);
          const r = R(['Under this yellow, look where {N} is: {p}.', "Let's check on {N} while we wait. He's {p}.", "Remember {N}? He's running {p} under the caution.", 'While they clean it up, {N} sits {p}.', 'A quick look at {N}: {p}.'], 'read-story', x);
          return { who: 'analyst', text: `${fill(r.item, { N: who(x), p: ordWord(p) })}${rm ? ` ${rm}` : ''}`, tpl: `${r.tpl}+remind-${s.kind}`, ids: [x], facts: [fact('race-status', `${who(x)} is running ${p}`, p, [x]), ...s.facts] };
        }
        if (k === 'pick' && pick && posNow(pick.driverId) > 0 && t >= Math.round(race.scheduledTurns * 0.25)) {
          const p = posNow(pick.driverId);
          called.add('read-pick');
          const r = R(['My pick, {N}, is running {p} under this caution.', 'For those keeping score on my pick: {N}, {p} right now.', '{N}, the man I picked, sits {p}.', "Checking on my pick. {N}'s {p}.", 'Where is my man {N}? {P}.', 'I had {N} before the green, and he is {p} right now.', 'How is my pick doing? {N}, {p}.', 'Since you asked, my pick {N} is {p}.', 'A quick look at my man {N}: {p}.'], 'read-pick', pick.driverId);
          return { who: 'analyst', text: fill(r.item, { N: pick.name, p: ordWord(p), P: cap(ordWord(p)) }), tpl: r.tpl, ids: [pick.driverId], facts: [fact('race-status', `${pick.name} is running ${p}`, p, [pick.driverId]), fact('take', `the analyst picked ${pick.name}`, 0, [pick.driverId])] };
        }
        if (k === 'points' && !pointsAired && t >= Math.ceil(race.scheduledTurns / 2)) {
          const live = pointsRead();
          if (live) { pointsAired = true; called.add('read-points'); return live; }
          const picture = pointsPictureLine(league?.season ?? undefined);
          if (!picture) continue;
          pointsAired = true;
          called.add('read-points');
          return { who: 'analyst', text: picture, tpl: 'points-picture', ids: [], facts: [fact('season', picture)] };
        }
        if (k === 'near' && people) {
          const pair = rivalsNear(state.grid.slice(0, 15).map(c => c.card.driverId), people.book, 2).find(p => !called.has(`near|${p.a}|${p.b}`));
          if (!pair) continue;
          const h = tellPair(pair.record);
          if (!h) continue;
          called.add('read-near'); called.add(`near|${pair.a}|${pair.b}`);
          const pa = posNow(pair.a), pb = posNow(pair.b);
          return { who: 'analyst', text: `Under yellow, ${sur(pair.a)} is ${ordWord(pa)} and ${sur(pair.b)} ${ordWord(pb)}. ${h.text}`, tpl: `read-near+${h.tpl}`, ids: [pair.a, pair.b], facts: [fact('race-status', `${who(pair.a)} ${pa}`, pa, [pair.a]), fact('race-status', `${who(pair.b)} ${pb}`, pb, [pair.b]), ...h.facts] };
        }
        if (k === 'anecdote' && league) {
          called.add('read-anecdote');
          const a = anecdote(cast, race.track.category, sk, seq);
          return { who: 'analyst', text: a.text, tpl: a.tpl, ids: [], facts: pastFacts(cast) };
        }
      }
      return null;
    }

    /** How the points fight stood coming in, and where those men are running right now. */
    function pointsRead(): { who: MemberId; text: string; tpl: string; ids: string[]; facts: Fact[] } | null {
      const where = (x: string) => {
        const p = posNow(x);
        return p > 0 ? ordWord(p) : state.retired.some(c => id(c) === x) ? 'out of the race' : null;
      };
      const p = league?.season?.playoff;
      if (p?.phase === 'regular-season' && p.racesToCut <= 10 && p.cutLine) {
        const out = p.bubble.find(b => b.side === 'out');
        const wi = where(p.cutLine.driverId), wo = out ? where(out.driverId) : null;
        if (!out || !wi || !wo) return null;
        const r = R(['Points check. {In} came in holding the last playoff spot, {Out} {gap} back. Right now {in} is {wi} and {out} is {wo}.', 'Keep an eye on the cut line. {Out} came in {gap} points short of {In}. Today {out} runs {wo}, {in} {wi}.', 'The playoff bubble right now: {in} {wi}, {out} {wo}. {Out} started the day {gap} back.', 'Here is what the cut line looks like. {In} holds the spot coming in, {Out} {gap} behind, and today {out} is {wo} with {in} {wi}.'], 'read-points', p.cutLine.driverId);
        return { who: 'analyst', text: fill(r.item, { In: p.cutLine.name, Out: out.name, in: sur(p.cutLine.driverId), out: sur(out.driverId), gap: -out.gap, wi, wo }), tpl: r.tpl, ids: [p.cutLine.driverId, out.driverId],
          facts: [fact('season', `${p.cutLine.name} holds the last playoff spot; ${out.name} ${-out.gap} back`, -out.gap, [p.cutLine.driverId, out.driverId]), fact('race-status', `${p.cutLine.name} ${posNow(p.cutLine.driverId)}`, posNow(p.cutLine.driverId), [p.cutLine.driverId]), fact('race-status', `${out.name} ${posNow(out.driverId)}`, posNow(out.driverId), [out.driverId])] };
      }
      const [first, second] = league?.table ?? [];
      if (!first || !second || (league?.raceNumber ?? 0) < 3) return null;
      const w1 = where(first.driverId), w2 = where(second.driverId);
      if (!w1 || !w2) return null;
      const m = first.points - second.points;
      if (m <= 0) return null;
      const r = R(['Points check. {L} came in leading {S} by {m}. Right now {l} is {w1} and {s} is {w2}.', 'A word on the standings: {L} by {m} over {S} coming in. Today it is {l} {w1}, {s} {w2}.', 'The points leader, {L}, is running {w1}. {S}, {m} back coming in, is {w2}.', "Let's check the championship. {L} came in {m} up on {S}. {l}'s {w1}, {s} {w2}.", '{S} started the day {m} behind {L} in the points. Right now: {s} {w2}, {l} {w1}.'], 'read-points', first.driverId);
      return { who: 'analyst', text: fill(r.item, { L: first.name, S: second.name, l: sur(first.driverId), s: sur(second.driverId), m, w1, w2 }), tpl: r.tpl, ids: [first.driverId, second.driverId],
        facts: [fact('season', `${first.name} led ${second.name} by ${m} points coming in`, m, [first.driverId, second.driverId]), fact('race-status', `${first.name} ${posNow(first.driverId)}`, posNow(first.driverId), [first.driverId]), fact('race-status', `${second.name} ${posNow(second.driverId)}`, posNow(second.driverId), [second.driverId])] };
    }
  }

  // ======================================================================
  // THE FLAG — and the wrap.
  // ======================================================================
  if (race.winnerId) {
    const W = who(race.winnerId), second = race.finishingOrder[1]?.driverId;
    const S = second ? who(second) : '';
    const photo = race.photoFinish?.leadChanged;
    const lastLeader = id(race.turns.at(-1)?.state.grid[0]);
    const called_ = race.called;
    const d = new Draft(5);
    d.energy = 3;
    const winFacts = [fact('race-winner', `${W} wins the ${race.state.raceName} at ${trackSpoken}`, 1, [race.winnerId]), ...(second ? [fact('race-status', `${S} finishes second`, 2, [second])] : [])];
    if (called_) {
      d.say('host', `${called_.reason === 'rain' ? 'The rain has the final say' : 'The race is called off'} — ${W} wins the ${race.state.raceName.replace(/^the /i, '')}${called_.reason === 'rain' ? ' in a rain-shortened race' : ''}, ${race.turnsRun} of ${race.scheduledTurns} turns complete${second ? `, with ${S} second` : ''}.`, 'called', [race.winnerId], [...winFacts, fact('race-status', `${race.turnsRun} of ${race.scheduledTurns} turns`, race.turnsRun), fact('race-status', `${race.scheduledTurns} scheduled`, race.scheduledTurns)]);
    } else if (photo && lastLeader) {
      const r = R(['At the line, it is {W}! The photo finish goes his way, and {X} loses it in the final contest.', '{W} wins it at the line! {X} led them to the flag and the photo finish took it away.', 'Photo finish, and it is {W}! {X} had it, and lost it at the stripe.'], 'photo');
      d.say('host', fill(r.item, { W, X: who(lastLeader) }), r.tpl, [race.winnerId, lastLeader], [...winFacts, fact('photo-finish', `${who(lastLeader)} led into the photo finish and lost`, 0, [lastLeader])]);
    } else {
      const r = R(C.FINISH[style].filter(x => S || !x.includes('{S}')), 'finish');
      d.say('host', fill(r.item, { W, S, race: race.state.raceName, track: trackSpoken }), r.tpl, [race.winnerId], winFacts);
    }
    // The analyst: what the win means first, then owning the pick.
    const tail: string[] = [];
    const tailFacts: Fact[] = [];
    const tailTpl: string[] = [];
    const meaning = finishSeasonLine(league?.season ?? undefined, race.winnerId, W);
    if (meaning) { tail.push(meaning.replace(/^It is /, "It's ").replace(/^That is /, "That's ")); tailTpl.push('meaning'); tailFacts.push(fact('season', meaning, 0, [race.winnerId])); }
    else {
      const start = starters.indexOf(race.winnerId);
      if (start >= 0 && !called_) { tail.push(`${W} started ${ordinal(start + 1)} and finishes first.`); tailTpl.push('started'); tailFacts.push(fact('race-field', `${W} started ${start + 1}`, start + 1, [race.winnerId])); }
    }
    if (pick) {
      const fin = race.finishingOrder.find(f => f.driverId === pick.driverId);
      const p = pick.driverId === race.winnerId ? pickNow('wins', pick.name, 1, sk, seq, sur(pick.driverId)) : pickNow('beaten', pick.name, fin && fin.status !== 'out' ? fin.place : null, sk, seq, sur(pick.driverId));
      if (!(pick.driverId !== race.winnerId && called.has('pick-out'))) {
        tail.push(p.text); tailTpl.push(p.tpl);
        tailFacts.push(fact('take', `the analyst picked ${pick.name}; finished ${fin?.place ?? 'out'}`, fin?.place ?? 0, [pick.driverId]));
      }
    }
    if (tail.length) d.say('analyst', tail.join(' '), tailTpl.join('+'), [race.winnerId], tailFacts);
    // The storylines it opened, paid off.
    const wraps = stories.map(s => resolveStoryline(s, race.finishingOrder, race.winnerId, sk, seq, sur)).filter((x): x is NonNullable<typeof x> => !!x).slice(0, 3);
    if (wraps.length) d.say('host', wraps.map(w => w.text).join(' '), wraps.map(w => w.tpl).join('+'), stories.flatMap(s => s.ids), wraps.flatMap(w => w.facts));
    roundup(d, C.CLOSE_ROUNDUP, 'close-roundup');
    if (league && league.seasonNumber > 0 && d.room()) {
      const r = league.next ? R(C.SIGN_OFF, 'signoff') : R(C.SIGN_OFF_LAST, 'signoff-last');
      d.say('host', fill(r.item, { H: cast.host.name, a: cast.analyst.short, p: cast.pit.short, next: league.next?.raceName ?? '' }), r.tpl, [], league.next ? [fact('history', `next race: the ${league.next.raceName}`, 0)] : [], true);
    }
    push(race.turnsRun, d, { headline: race.called?.reason === 'rain' ? 'Rain-shortened' : race.called ? 'Race called' : photo ? 'Decided at the line' : `${W} wins`, category: 'finish', stakes: '' }, { kind: 'close', citation: race.photoFinish?.ruleCitation ?? null });
  }
  const bible: StoryBible = buildBible({ league, cast, stories, pick, pickLine, entered: new Set(starters.length ? starters : allCars.map(c => c.card.driverId)), nameOf: who, shortOf: sur, venue, key: sk, seq });
  return { raceName: race.state.raceName, register: registerOf(race.track), lapsPerTurn: lapsPerTurn(race.track), beats, eventsInRace: race.events.length, turnsRun: race.turnsRun, cast, bible };
}

/**
 * When a storyline driver LEADS or climbs into the top three: the booth
 * remembers why he matters. Only said at the front, so "right where he wants
 * to be" is always true when it airs.
 */
function storyCallback(s: Storyline, subject: string, who: (id: string) => string, key: string, seq: number): { text: string; tpl: string; facts: Fact[] } | null {
  const N = who(subject), n = s.n ?? 0;
  const first = s.ids[0] === subject;
  const banks: Partial<Record<Storyline['kind'], string[]>> = {
    'first-win': ['{N} has never won in {n} starts. Never.', 'That is the man still chasing a first win, {n} starts in.', 'Remember, {N} is {n} starts into his career without a win.'],
    drought: ["{N} hasn't won in {n} starts. He's hungry.", "{N}, {n} starts since his last win, and look where he is.", "That's {N}, and it's been {n} starts since he won. This is what he's been waiting for."],
    streak: ['{N} has won {n} straight coming in. Here he is again.', 'The man on the {n}-race streak is right up front again.'],
    points: first
      ? ['That is the points leader, up front where he wants to be.', 'The man leading the standings, leading the race.']
      : ['That is the man second in the points coming in. This is how you close a gap.', 'Second in the standings coming in, and up front today.'],
    bubble: ['That is a man fighting for a playoff spot. Every position counts for him.', 'For a driver on the playoff bubble, this is a big day.'],
    defending: ['He won this race last time. He knows the way.', 'The defending winner of this race, right up front.'],
    track: ['Told you he likes this place.', 'He has won here before, and it shows.'],
    champion: ['The champion. You knew he would be up there.', 'That is what champions do.'],
    'last-week': ["Last week's winner, at it again.", 'He won last week, and he is right back up front.'],
    rivalry: ['And there is one of the two we talked about before the green.', "Remember the history from before the green? That's one of them."],
    milestone: ['A win today would be a milestone for him.', 'He is one win from a career milestone.'],
  };
  const bank = banks[s.kind];
  if (!bank) return null;
  const r = rotate(bank, `cb|${key}|${s.key}|${subject}`, seq);
  const text = fillSlots(r.item, { N, n });
  const facts: Fact[] = n ? [fact('storyline', `${N}: ${n}`, n, [subject])] : [];
  // The ordinals the callbacks say: a FIRST win, SECOND in the points.
  if (s.kind === 'first-win') facts.push(fact('storyline', `${N} is still looking for his first win`, 1, [subject]));
  if (s.kind === 'points') facts.push(fact('storyline', `${N} came in ${first ? 'first' : 'second'} in the points`, first ? 1 : 2, [subject]));
  return { text, tpl: `cb-${s.kind}#${r.index}`, facts };
}

/** Under the caution, a neutral reminder of why a storyline driver matters — true wherever he is running. */
function storyReminder(s: Storyline, who: (id: string) => string): string | null {
  const n = s.n ?? 0;
  const x = s.ids[0];
  switch (s.kind) {
    case 'first-win': return `He has never won in ${n} starts.`;
    case 'drought': return `He has not won in ${n} starts.`;
    case 'streak': return `He came in having won ${n} straight.`;
    case 'points': return 'He came in leading the points.';
    case 'bubble': return 'He came in holding the last playoff spot.';
    case 'defending': return 'He won this race last time.';
    case 'track': return `He has won here ${n} times.`;
    case 'champion': return 'He is the defending champion.';
    case 'last-week': return 'He won last week.';
    case 'milestone': return 'One more win is a career milestone for him.';
    case 'rivalry': return s.ids[1] ? `Keep ${who(s.ids[1])} in mind too.` : null;
    default: return x ? null : null;
  }
}
